import React, {useEffect, useState} from 'react'
import Button from '@components/Button'
import createOrder from '@components/Checkout/Confirm/createOrder'
import getOptions from '@components/Checkout/Confirm/getOptions'
import Bags from '@components/Money/Bags'
import {EVENTS, sendEvent} from '@helpers/events'
import deliveryFeeText from '@helpers/misc/deliveryFeeText'
import useTrackEcommerceEvent, {ECOMMERCE_EVENTS} from '@helpers/track/useTrackEcommerceEvent'
import useIdempotencyKey from '@hooks/useIdempotencyKey'
import useIsSpecialLayout from '@hooks/useIsSpecialLayout'
import useSessionStorageState from '@hooks/useSessionStorageState'
import useTimeField from '@hooks/useTimeField'
import {DeliveryTimeTypes} from '@hooks/useTimeField/types'
import useUserId from '@hooks/useUserId'
import useWebsiteInfo from '@hooks/useWebsiteInfo'
import formatNumber from '@i18n/formatNumber'
import FormatNumber from '@i18n/formatNumber/Component'
import {DELIVERY_TIME_TYPE_SELECTOR_ID} from '@page-components/Checkout/Checkout/Delivery/Duration/Update/TimeField'
import {useMutate} from 'apollo-hooks'
import {useApolloQuery as useQuery} from 'apollo-hooks'
import classnames from 'classnames'
import gql from 'graphql-tag'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import {useRouter} from 'next/router'
import {useTranslation} from 'next-i18next'

import styles from './styles.module.css'

const SignTerms = dynamic(() => import('@components/SignTerms'), {ssr: false})
const DynamicDelivery = dynamic(() => import('@components/Money/DynamicDelivery'))
const SendingToKitchen = dynamic(() => import('@components/SendingToKitchen'))

function getOffset(htmlElement) {
  const rect = htmlElement.getBoundingClientRect()
  return {
    left: rect.left + window.scrollX,
    top: rect.top + window.scrollY
  }
}

export default function Resume(props) {
  const {cart, preferences, website, whatsappChatCommerceNumber} = props
  const {deliveryTimeTimeOptions: timeOptions, deliveryTimeType} = useTimeField()
  const {itemsPrice, realPrice, totalPrice, bagAmount, items, deliveryType, storeId} = cart
  const mutate = useMutate()
  const {t} = useTranslation()
  const i18nWebsite = 'website:order.cart.content.resume'
  const i18nGeneric = 'generic:fields'
  const {idempotencyKey, regenerateIdempotencyKey} = useIdempotencyKey()
  const [errorMessage, setErrorMessage] = useState()
  const [, setVerifyPhone] = useState()
  const [, setCreateCard] = useState()
  const [sendingToKitchen, setSendingToKitchen] = useState(false)
  const {options} = getOptions(props)
  const router = useRouter()
  const isSpecialLayout = useIsSpecialLayout()
  const [onTable] = useSessionStorageState('onTable')
  const [orderGroupId] = useSessionStorageState('orderGroupId')
  const [redirecting, setRedirecting] = useState(false)
  const {websiteId, websiteName} = useWebsiteInfo()
  const userId = useUserId()
  const checkoutPath = '/checkout'

  useEffect(() => {
    router.prefetch(checkoutPath)
  }, [router, checkoutPath])

  const {pendingTermInfos, me} = useQuery({
    query: gql`
      query pendingTermsCart($userId: String, $email: String, $websiteId: String) {
        pendingTermInfos(userId: $userId, email: $email, websiteId: $websiteId)
        me {
          _id
          name
        }
      }
    `,
    variables: {userId, websiteId, email: preferences.sessionEmail},
    partial: true
  })

  const trackEcommerceEvent = useTrackEcommerceEvent()

  const hasNoName = (!me && !preferences.sessionEmailHasName) || (me && me.name === 'Sin nombre')
  const requiredTermInfos = (pendingTermInfos || []).filter(term => !term.optional)
  const requiredTermVersions = requiredTermInfos.map(term => term.latestVersion)
  const [termSignatures, setTermSignatures] = useState([])
  const signedTermVersions = termSignatures.map(term => term.version)
  const missingSignatures = requiredTermVersions.some(
    version => !signedTermVersions.includes(version)
  )

  const goToReview = () => {
    setRedirecting(true)
    trackEcommerceEvent(ECOMMERCE_EVENTS.INITCHECKOUT, {cart})
    sendEvent(EVENTS.clicked.cartContinueToCheckout)
    router.push(checkoutPath)
  }

  const sendToKitchen = async () => {
    const finalOptions = {
      ...options,
      idempotencyKey,
      termSignatures: missingSignatures ? {} : termSignatures,
      paymentType: 'inStore',
      orderGroupId,
      isTableOrderV2: true
    }
    const order = await createOrder({
      mutate,
      history: router,
      options: finalOptions,
      setErrorMessage,
      setVerifyPhone,
      setCreateCard,
      regenerateIdempotencyKey
    })
    if (order) {
      router.push(`/checkout-on-table/prepare`)
    }
  }

  const showSendToKitchen = () => setSendingToKitchen(true)

  const goToWhatsapp = () => {
    setRedirecting(true)
    trackEcommerceEvent(ECOMMERCE_EVENTS.INITCHECKOUT, {cart})
    sendEvent(EVENTS.clicked.cartContinueToCheckout, {
      chatCommerceType: 'whatsapp'
    })

    if (!whatsappChatCommerceNumber) {
      const justoBotPhoneNumber = '18453032928' // TODO: CRUD and fetch from backend
      router.push(`/whatsapp-redirect?phone=${justoBotPhoneNumber}&text=Completar`)
      return
    }

    router.push(
      `/whatsapp-redirect?phone=${whatsappChatCommerceNumber.replace('+', '')}&text=Completar`
    )
  }

  const hasAnyOutOfStock = () => {
    for (const item of items) {
      if (item.isOutOfStock) return true
    }
    return false
  }

  const amountToReachMinimum = () => {
    if (!website) return
    if (!website.minimumOrderPrice) return
    if (!itemsPrice) return
    const amount = website.minimumOrderPrice - itemsPrice
    if (amount > 0) return amount
    return 0
  }

  const renderDiscount = () => {
    if (itemsPrice === realPrice) return

    return (
      <div className={styles.discount}>
        <div className={styles.label}>{t(`${i18nWebsite}.labelDiscount`)}</div>
        <div>
          - <FormatNumber value={realPrice - itemsPrice} />{' '}
        </div>
      </div>
    )
  }

  const renderSubTotal = () => {
    if (onTable) return null

    return (
      <div className={styles.itemsPrice}>
        <div className={styles.label}>{t(`${i18nWebsite}.labelSubtotal`)}</div>
        <div>{formatNumber(realPrice, 'money')}</div>
      </div>
    )
  }

  const renderDeliveryFee = () => {
    if (deliveryType !== 'delivery') return null
    if (!storeId) return null
    const {deliveryFee} = cart
    const {address} = preferences

    const dynamicFee =
      props.preferences &&
      props.preferences.store &&
      props.preferences.store.currentExtraDeliveryCost > 0

    const deliveryContent = deliveryFeeText({address, deliveryFee})

    return (
      <div className={styles.itemsPrice}>
        <div className={styles.label}>{t(`${i18nGeneric}.dispatch`)}</div>
        <div>{dynamicFee ? <DynamicDelivery value={deliveryContent} /> : deliveryContent}</div>
      </div>
    )
  }

  const renderButton = () => {
    const disabled = hasAnyOutOfStock()
    if (disabled) {
      return (
        <div className={styles.button}>
          <Button primary disabled>
            {t(`${i18nWebsite}.labelProductsOutStock`)}
          </Button>
        </div>
      )
    }

    // if there's no available time options for scheduling
    const hasNoOptions = timeOptions && timeOptions.length === 0
    if (deliveryTimeType?.value === DeliveryTimeTypes.SCHEDULE_ORDER && hasNoOptions) {
      const scrollToDeliveryTimeTypeSelector = () => {
        const deliveryTimeSelector = document.querySelector(`#${DELIVERY_TIME_TYPE_SELECTOR_ID}`)
        if (deliveryTimeSelector) {
          window.scroll({
            top: getOffset(deliveryTimeSelector).top - 20,
            left: 0,
            behavior: 'smooth'
          })
        }
      }

      return (
        <div className={styles.button} onClick={scrollToDeliveryTimeTypeSelector}>
          <Button primary disabled>
            {t(`${i18nWebsite}.labelNoSchedulingOptions`)}
          </Button>
        </div>
      )
    }

    const amountToReach = amountToReachMinimum()

    if (amountToReach) {
      return (
        <div className={styles.button}>
          <Button primary disabled>
            {t(`${i18nWebsite}.labelNotReachedMin`)}
          </Button>
          <div className="description" style={{marginTop: 5}}>
            {t(`${i18nWebsite}.labelAddMoreProducts`, {
              money: formatNumber(amountToReach, 'money')
            })}
          </div>
        </div>
      )
    }

    if (onTable) {
      return (
        <div className={styles.button}>
          <SignTerms
            websiteId={websiteId}
            websiteName={websiteName}
            termInfos={pendingTermInfos}
            termSignatures={termSignatures}
            setTermSignatures={setTermSignatures}
          />
          {hasNoName ? (
            <p>
              No tenemos tu nombre, por favor ve a <br />
              <Link href="/settings">Mi cuenta</Link> para actualizar tu información
            </p>
          ) : null}

          <Button
            fullWidth
            primary
            onClick={showSendToKitchen}
            disabled={missingSignatures || hasNoName}>
            {t(`${i18nWebsite}.labelSendToKitchen`)}
          </Button>
        </div>
      )
    }

    if (isSpecialLayout === 'chat') {
      return (
        <div className={classnames(styles.button, styles.whatsappButton)}>
          <Button primary onClick={goToWhatsapp} loading={redirecting}>
            {t(`${i18nWebsite}.labelCompleteOrderInWhatsapp`)}
          </Button>
        </div>
      )
    }

    return (
      <div className={styles.button}>
        <Button primary onClick={goToReview} loading={redirecting}>
          {t(`${i18nWebsite}.labelContinueWithMyOrder`)}
        </Button>
      </div>
    )
  }

  if (sendingToKitchen) {
    return (
      <SendingToKitchen
        order={cart}
        sendToKitchen={sendToKitchen}
        cancelSending={() => setSendingToKitchen(false)}
      />
    )
  }

  return (
    <div className={styles.container}>
      {errorMessage ? <div className={styles.error}>{errorMessage}</div> : null}
      {renderSubTotal()}
      {renderDeliveryFee()}
      {renderDiscount()}
      <Bags preferences={preferences} />
      <div className={onTable ? styles.totalOnTable : styles.total}>
        <div className={styles.label}>{t(`${i18nWebsite}.labelTotal`)}</div>
        <div>
          <FormatNumber value={totalPrice + bagAmount} />{' '}
        </div>
      </div>
      {renderButton()}
    </div>
  )
}
